import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../lib/formatters.js';

interface AiCheck {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

interface AiProposalCardProps {
  verdict: string;
  confidence: number;
  reasoning: string;
  recommendedDecision: string;
  recommendedAmount?: number | null;
  isoCurrencyCode: string;
  checks: AiCheck[];
}

const VERDICT_BANNER: Record<string, string> = {
  AUTO_APPROVABLE: 'bg-success/10 border-success/30 text-success',
  NEEDS_REVIEW: 'bg-copper-soft border-copper/30 text-copper-deep',
  INSUFFICIENT_EVIDENCE: 'bg-copper-soft border-copper/20 text-copper-deep',
  LIKELY_REJECT: 'bg-danger/10 border-danger/30 text-danger',
};

const VERDICT_BAR: Record<string, string> = {
  AUTO_APPROVABLE: 'bg-success',
  NEEDS_REVIEW: 'bg-copper',
  INSUFFICIENT_EVIDENCE: 'bg-copper',
  LIKELY_REJECT: 'bg-danger',
};

const OUTCOME_ICON: Record<string, string> = {
  PASS: '✓',
  WARN: '⚠',
  FAIL: '✗',
  INDETERMINATE: '—',
};

const OUTCOME_COLOR: Record<string, string> = {
  PASS: 'text-success',
  WARN: 'text-copper',
  FAIL: 'text-danger',
  INDETERMINATE: 'text-slate-400',
};

export function AiProposalCard({
  verdict,
  confidence,
  reasoning,
  recommendedDecision,
  recommendedAmount,
  isoCurrencyCode,
  checks,
}: AiProposalCardProps) {
  const { t } = useTranslation();
  const pct = Math.round(confidence * 100);
  const bannerClass = VERDICT_BANNER[verdict] ?? VERDICT_BANNER['NEEDS_REVIEW'];
  const barColor = VERDICT_BAR[verdict] ?? 'bg-copper';

  return (
    <div className="border border-hairline rounded overflow-hidden bg-bone">
      {/* Verdict banner */}
      <div className={['px-4 py-3 border-b', bannerClass].join(' ')}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
            {t('wizard.labels.finalVerdict')}
          </span>
          <span className="text-xs font-mono font-medium">
            {t('wizard.labels.overallConfidence')}: <strong>{pct}%</strong>
          </span>
        </div>
        <p className="font-semibold mt-0.5 text-sm">
          {t(`wizard.verdicts.${verdict}`, { defaultValue: verdict })}
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Confidence bar */}
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{t('wizard.labels.overallConfidence')}</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
            <div
              className={['h-full rounded-sm transition-all', barColor].join(' ')}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Reasoning */}
        <p className="text-sm text-ink leading-relaxed">{reasoning}</p>

        {/* Recommended decision + amount */}
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('approver.aiProposal.recommendation', { defaultValue: 'Recommendation' })}
          </span>
          <span className="font-semibold text-sm text-ink">
            {t(`approver.decisions.${recommendedDecision}`, { defaultValue: recommendedDecision })}
          </span>
          {recommendedAmount != null && (
            <span className="font-mono text-sm text-ink">
              {formatCurrency(recommendedAmount, isoCurrencyCode)}
            </span>
          )}
        </div>

        {/* Findings collapsible */}
        {checks.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-copper transition-colors select-none">
              <span className="group-open:hidden">▶</span>
              <span className="hidden group-open:inline">▼</span>
              {t('wizard.labels.verificationChecks')} ({checks.length})
            </summary>
            <div className="mt-3 space-y-1.5">
              {checks.map((c, i) => {
                const icon = OUTCOME_ICON[c.outcome] ?? '—';
                const color = OUTCOME_COLOR[c.outcome] ?? 'text-slate-400';
                const confPct = Math.round(c.confidence * 100);

                return (
                  <div
                    key={i}
                    className="flex items-start gap-2 px-3 py-2 border border-hairline rounded bg-bone-soft text-sm"
                  >
                    <span className={['font-bold shrink-0 w-4 mt-0.5 text-xs', color].join(' ')}>
                      {icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-xs text-ink">
                        {t(`wizard.findings.checkTypes.${c.checkType}`, {
                          defaultValue: c.checkType,
                        })}
                      </span>
                      <span
                        className="text-xs text-slate-500 block mt-0.5 leading-snug"
                        title={c.reasoning}
                      >
                        {c.reasoning}
                      </span>
                    </div>
                    <span className="text-xs font-mono text-slate-400 shrink-0">{confPct}%</span>
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
